/**
 * Staff Launch Handler Page (MX-Auth Node 7, browser-token-storage-hardening).
 *
 * Terminal step of the FI-staff MX-Auth handoff. An upstream MX-Auth Connect
 * widget runs the member bank-login (consumer-api
 * POST /api/v1/mx-auth/staff/session -> Quiltt Connect ->
 * POST /api/v1/mx-auth/staff/connect-result), receives the one-time
 * `handoffCode` on the MxAuthStaffAuthResponse, and redirects the browser here
 * as `/launch?code=<handoffCode>` (on failure: `?error=<code>&message=<...>`).
 *
 * This page POSTs the code to same-origin POST /api/v1/auth/exchange
 * (credentials: 'include'); aaim-core consumes the single-use code and sets the
 * HttpOnly session cookies on admin.aaim.com, returning a body with NO tokens.
 * It then hydrates the user from GET /api/v1/auth/me and applies the USER record
 * only via loginWithTokens (which ignores token args and normalizes roles to
 * lowercase). The SPA never receives a bearer -- the session lives only in the
 * HttpOnly cookie. SEC-V04 / .claude/rules/no-browser-token-storage.md.
 *
 * Mirrors the shipped OAuthCallback / SSOCallback cookie cutover (N6).
 */

import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Heading, BodyText, Card } from '@aaimplatform/ui';
import { useAuthStore, type User } from '../stores/auth';

export function StaffLaunchPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState<string | null>(null);
  // Inline UI state-machine union (no named ad-hoc type per the OpenAPI-first gate).
  const [status, setStatus] = useState<'exchanging' | 'loading_user' | 'redirecting' | 'error'>('exchanging');

  useEffect(() => {
    // The upstream MX-Auth Connect widget surfaces a failure (anti-spoof,
    // no-invite, connection-read error) as ?error=<code>&message=<...>.
    const upstreamError = searchParams.get('error');
    if (upstreamError) {
      setError(searchParams.get('message') ?? 'Staff sign-in could not be completed.');
      setStatus('error');
      return;
    }

    const code = searchParams.get('code');
    if (!code) {
      setError(
        'No staff handoff code received. Return to your institution portal and try again.',
      );
      setStatus('error');
      return;
    }

    void (async () => {
      try {
        // 1. Exchange the one-time handoff code for the HttpOnly session cookies.
        //    aaim-core consumeCode + setSessionCookies on admin.aaim.com; the
        //    response body carries NO tokens.
        setStatus('exchanging');
        const exchangeRes = await fetch('/api/v1/auth/exchange', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ code }),
        });
        if (!exchangeRes.ok) {
          setError('Staff sign-in could not be completed. Please try again.');
          setStatus('error');
          return;
        }

        // 2. Hydrate the user from the freshly-set cookie. The
        //    @aaimplatform/database cookie->Bearer shim resolves the session
        //    server-side; no Authorization header is attached.
        setStatus('loading_user');
        const meRes = await fetch('/api/v1/auth/me', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });
        if (!meRes.ok) {
          setError(
            'Staff sign-in succeeded but the session could not be loaded. Please try again.',
          );
          setStatus('error');
          return;
        }
        const data = (await meRes.json()) as { user?: User };
        if (!data.user) {
          setError('Staff sign-in succeeded but no user profile was returned.');
          setStatus('error');
          return;
        }

        // 3. Apply the USER record only. loginWithTokens ignores any token args
        //    and normalizes roles to lowercase (authz-case-insensitive.md). The
        //    SPA holds NO bearer; the session lives only in the HttpOnly cookie.
        useAuthStore.getState().loginWithTokens({ user: data.user });

        // 4. Strip the handoff code from the URL/history, then enter the portal.
        window.history.replaceState(null, '', '/launch');
        setStatus('redirecting');
        navigate('/', { replace: true });
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Staff launch failed');
        setStatus('error');
      }
    })();
  }, [searchParams, navigate]);

  const getStatusMessage = () => {
    switch (status) {
      case 'exchanging':
        return 'Completing secure sign-in...';
      case 'loading_user':
        return 'Loading your profile...';
      case 'redirecting':
        return 'Redirecting to admin portal...';
      default:
        return 'Processing...';
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
      <div className="mt-16 flex flex-col items-center">
        <Card className="w-full text-center">
          {status !== 'error' ? (
            <>
              <div className="w-16 h-16 border-4 border-accent border-t-transparent rounded-full animate-spin mx-auto mb-6" />
              <Heading level={2} size="section" className="font-sans mb-4">Aaim Administration Portal</Heading>
              <BodyText className="text-secondary mb-4">
                {getStatusMessage()}
              </BodyText>
              <BodyText className="text-tertiary text-sm">
                Securely connecting from your institution portal
              </BodyText>
            </>
          ) : (
            <>
              <Alert type="error" className="mb-6">
                {error}
              </Alert>
              <BodyText className="text-secondary mb-4">
                Please return to your institution portal and try again.
              </BodyText>
              {/* GH #1962: was "contact Aaim support" -- a dead end, and worse
                  here because this is a pre-auth handoff-failure state: the
                  staffer has no session, so the auth-gated in-app request form is
                  unreachable. The real resolver is their FI administrator, who
                  issued the invitation and owns the IdP mapping. */}
              <BodyText className="text-tertiary text-sm">
                If this problem persists, your institution's administrator can
                re-send your staff invitation.
              </BodyText>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
